import React, { useState, useEffect, useRef } from 'react';
import { Calendar, X, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import ReCAPTCHA from 'react-google-recaptcha';

const initialFormData = {
  referralSource: 'Self',
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  gender: '',
  email: '',
  mobile: '',
  referrerName: '',
  referrerEmail: '',
  notes: ''
};

const initialErrors = {
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  email: '',
  mobile: ''
};

// ── Validators ────────────────────────────────────────────────────────────────

const validateName = (value, fieldLabel) => {
  if (!value.trim()) return `${fieldLabel} is required.`;
  if (value.trim().length < 2) return `${fieldLabel} must be at least 2 characters.`;
  if (value.trim().length > 50) return `${fieldLabel} must be under 50 characters.`;
  if (!/^[a-zA-Z\s'\-]+$/.test(value.trim()))
    return `${fieldLabel} can only contain letters, spaces, hyphens, or apostrophes.`;
  return '';
};

const validateEmail = (value) => {
  if (!value) return ''; // optional field
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim()))
    return 'Please enter a valid email address.';
  return '';
};

const validateMobile = (value) => {
  if (!value.trim()) return 'Mobile number is required.';
  const digits = value.replace(/[\s\-().+]/g, '');
  if (!/^\d+$/.test(digits)) return 'Mobile number can only contain digits, spaces, +, -, or parentheses.';
  if (digits.length < 7 || digits.length > 15)
    return 'Mobile number must be between 7 and 15 digits.';
  return '';
};

const validateDOB = (value) => {
  if (!value) return 'Date of birth is required.';
  const dob = new Date(value);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (isNaN(dob.getTime())) return 'Please enter a valid date.';
  if (dob >= today) return 'Date of birth must be in the past.';
  const minDate = new Date();
  minDate.setFullYear(minDate.getFullYear() - 120);
  if (dob < minDate) return 'Please enter a realistic date of birth.';
  return '';
};

// ─────────────────────────────────────────────────────────────────────────────

const AppointmentModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState(initialErrors);
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaChecked, setCaptchaChecked] = useState(false);
  const [captchaError, setCaptchaError] = useState('');
  const [captchaRenderKey, setCaptchaRenderKey] = useState(0);
  const recaptchaRef = useRef(null);

  const apiBaseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000')
    .trim()
    .replace(/^['\"]|['\"]$/g, '');
  const siteKey = (process.env.REACT_APP_RECAPTCHA_SITE_KEY || '')
    .trim()
    .replace(/^['\"]|['\"]$/g, '');

  // ── Reset on open ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      setFormData(initialFormData);
      setErrors(initialErrors);
      setTouched({});
      setSubmitSuccess(false);
      setIsSubmitting(false);
      setCaptchaToken('');
      setCaptchaChecked(false);
      setCaptchaError('');
      if (recaptchaRef.current) recaptchaRef.current.reset();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!siteKey) setCaptchaError('Captcha key missing');
    else setCaptchaError('');
  }, [siteKey]);

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const resetCaptcha = (message) => {
    setCaptchaToken('');
    setCaptchaChecked(false);
    if (message) setCaptchaError(message);
    if (recaptchaRef.current) recaptchaRef.current.reset();
    setCaptchaRenderKey((prev) => prev + 1);
  };

  const getFieldError = (name, value) => {
    switch (name) {
      case 'firstName':  return validateName(value, 'First name');
      case 'lastName':   return validateName(value, 'Last name');
      case 'email':      return validateEmail(value);
      case 'mobile':     return validateMobile(value);
      case 'dateOfBirth': return validateDOB(value);
      default:           return '';
    }
  };

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Live validation once the field has been touched
    if (touched[name]) {
      setErrors((prev) => ({ ...prev, [name]: getFieldError(name, value) }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({ ...prev, [name]: getFieldError(name, value) }));
  };

  const validateAll = () => {
    const newErrors = {
      firstName:   validateName(formData.firstName, 'First name'),
      lastName:    validateName(formData.lastName, 'Last name'),
      dateOfBirth: validateDOB(formData.dateOfBirth),
      email:       validateEmail(formData.email),
      mobile:      validateMobile(formData.mobile),
    };
    setErrors(newErrors);
    // Mark all validated fields as touched so errors show
    setTouched({ firstName: true, lastName: true, dateOfBirth: true, email: true, mobile: true });
    return Object.values(newErrors).every((e) => e === '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCaptchaError('');

    if (!validateAll()) return;

    if (!captchaToken) {
      setCaptchaChecked(false);
      setCaptchaError('Please complete the captcha checkbox.');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        dob: formData.dateOfBirth || null,
        gender: formData.gender || null,
        email: formData.email || null,
        mobile: formData.mobile,
        referrer_name: formData.referrerName?.trim() || 'Self',
        referrer_email: formData.referrerEmail || null,
        notes: formData.notes || null,
        referral_source: 'Self',
        captcha_token: captchaToken
      };

      const resp = await fetch(`${apiBaseUrl}/api/personal-referrals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!resp.ok) {
        const errorBody = await resp.text();
        throw new Error(errorBody || 'submission failed');
      }

      setSubmitSuccess(true);
      resetCaptcha('');
    } catch (err) {
      console.error(err);
      if (String(err?.message || '').toLowerCase().includes('captcha')) {
        resetCaptcha('reCAPTCHA timed out. Please verify again.');
      }
      alert('There was a problem submitting the form');
      return;
    } finally {
      setIsSubmitting(false);
    }

    // setTimeout(() => {
    //   onClose();
    //   setSubmitSuccess(false);
    //   setFormData(initialFormData);
    //   resetCaptcha('');
    // }, 2000);
  };

  if (!isOpen) return null;

  // ── Shared field-error helper for rendering ─────────────────────────────────
  const FieldError = ({ field }) =>
    errors[field] ? (
      <p className="text-red-500 text-xs mt-1" role="alert">{errors[field]}</p>
    ) : null;

  const inputClass = (field) =>
    `h-11 ${errors[field] ? 'border-red-500 focus:ring-red-500' : ''}`;

  const showCaptchaBorder =
    Boolean(captchaError) && !/(timed out|timeout)/i.test(captchaError);

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-2 sm:p-4 overflow-y-auto">
      <div className="bg-white rounded-xl sm:rounded-2xl w-full max-w-4xl shadow-2xl my-4">

        {/* Header */}
        <div className="bg-gradient-to-r from-[#1B1B3A] to-[#2D2D4A] px-6 py-4 rounded-t-xl sm:rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-[#E63946] p-2 rounded-lg">
                <Calendar className="h-5 w-5 text-white" />
              </div>
              <h2 className="text-xl font-bold text-white">Schedule Your Consultation Today</h2>
            </div>
            <button
              onClick={onClose}
              className="text-white/70 hover:text-white transition-colors p-1"
              data-testid="close-appointment-modal"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Body */}
        {submitSuccess ? (
          <div className="p-8 sm:p-12 text-center">
            <div className="bg-green-100 w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-7 w-7 sm:h-8 sm:w-8 text-green-600" />
            </div>
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
              Appointment Request Submitted!
            </h3>
            <p className="text-gray-600 text-sm">
              Our team will contact you within 24 hours to confirm your appointment.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6" noValidate>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">

              {/* First Name */}
              <div>
                <Label className="text-gray-700 text-sm font-medium mb-2 block">
                  First Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  className={inputClass('firstName')}
                  aria-invalid={!!errors.firstName}
                  data-testid="appointment-first-name-input"
                />
                <FieldError field="firstName" />
              </div>

              {/* Last Name */}
              <div>
                <Label className="text-gray-700 text-sm font-medium mb-2 block">
                  Last Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  className={inputClass('lastName')}
                  aria-invalid={!!errors.lastName}
                  data-testid="appointment-last-name-input"
                />
                <FieldError field="lastName" />
              </div>

              {/* Date of Birth */}
              <div>
                <Label className="text-gray-700 text-sm font-medium mb-2 block">
                  Date of Birth <span className="text-red-500">*</span>
                </Label>
                <Input
                  name="dateOfBirth"
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  className={inputClass('dateOfBirth')}
                  aria-invalid={!!errors.dateOfBirth}
                  data-testid="appointment-dob-input"
                />
                <FieldError field="dateOfBirth" />
              </div>

              {/* Gender */}
              <div>
                <Label className="text-gray-700 text-sm font-medium mb-2 block">Gender</Label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                  className="w-full h-11 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#20B2AA] focus:border-transparent"
                  data-testid="appointment-gender-select"
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Email */}
              <div>
                <Label className="text-gray-700 text-sm font-medium mb-2 block">Email</Label>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  className={inputClass('email')}
                  aria-invalid={!!errors.email}
                  data-testid="appointment-email-input"
                />
                <FieldError field="email" />
              </div>

              {/* Mobile */}
              <div>
                <Label className="text-gray-700 text-sm font-medium mb-2 block">
                  Mobile <span className="text-red-500">*</span>
                </Label>
                <Input
                  name="mobile"
                  type="tel"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  className={inputClass('mobile')}
                  aria-invalid={!!errors.mobile}
                  data-testid="appointment-mobile-input"
                />
                <FieldError field="mobile" />
              </div>

              {/* Notes */}
              <div className="md:col-span-2 lg:col-span-3">
                <Label className="text-gray-700 text-sm font-medium mb-2 block">Notes</Label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Please describe your symptoms or reason for visit..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#20B2AA] focus:border-transparent resize-y"
                  data-testid="appointment-notes-input"
                />
              </div>

              {/* reCAPTCHA */}
              <div className="md:col-span-2 lg:col-span-3">
                <div className={`${showCaptchaBorder ? 'border-2 border-red-500 rounded-lg p-2' : ''}`}>
                  {siteKey ? (
                    <ReCAPTCHA
                      key={captchaRenderKey}
                      ref={recaptchaRef}
                      sitekey={siteKey}
                      onChange={(token) => {
                        setCaptchaToken(token || '');
                        setCaptchaChecked(!!token);
                        setCaptchaError('');
                      }}
                      onExpired={() => resetCaptcha('reCAPTCHA timed out. Please verify again.')}
                      onErrored={() => resetCaptcha('reCAPTCHA timeout or network issue. Please try again.')}
                      data-testid="appointment-recaptcha"
                    />
                  ) : null}
                </div>
                {captchaError && (
                  <p className="text-red-500 text-sm mt-2">{captchaError}</p>
                )}
              </div>
            </div>

            {/* Footer Buttons */}
            <div className="flex justify-end gap-3 pt-5 mt-5 border-t border-gray-200">
              <Button
                type="button"
                variant="outline"
                className="h-11 px-6 bg-gray-600 text-white hover:bg-gray-700 border-none rounded-full"
                onClick={onClose}
                data-testid="cancel-appointment-btn"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-[#E63946] hover:bg-[#D32F3D] text-white font-semibold h-11 px-8 rounded-full"
                disabled={isSubmitting || !captchaChecked || !siteKey}
                data-testid="submit-appointment-btn"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Submitting...
                  </>
                ) : (
                  'Request Appointment'
                )}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AppointmentModal;